<script setup>
import {computed} from 'vue'

const props = defineProps({
  pages: {
    type: Number,
    required: true,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
})
const emit = defineEmits(['paginate'])

const hasPrevPage = computed(() => {
  return props.currentPage > 1
})
const hasNextPage = computed(() => {
  return props.currentPage < props.pages
})
const numberedPages = computed(() => {
  return Array.from({length: props.pages}, (_, i) => i + 1)
})
const visiblePages = computed(() => {
  return props.currentPage === 1
      ? numberedPages.value.slice(0, 3)
      : (props.currentPage === props.pages ? numberedPages.value.slice(-3) : numberedPages.value.slice(props.currentPage - 2, (props.currentPage - 2) + 3))
})

function prevPage() {
  emit('paginate', props.currentPage - 1)
}
function nextPage() {
  emit('paginate', props.currentPage + 1)
}
function goToPage(p) {
  emit('paginate', p)
}
</script>

<template>
  <div class="pagination">
    <a href="#" class="pagination__button pagination__button--first" @click.prevent="goToPage(1)" v-if="hasPrevPage" aria-label="First Page">
      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="12" viewBox="0 0 9 12" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.94979 6.65685L1.00004 1.70711L1.70715 1L6.65689 5.94975L5.94979 6.65685Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1 10.1925L5.94975 5.24272L6.65689 5.94975L1.70711 10.8996L1 10.1925Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M6.99991 11L6.99991 1L7.99991 1L7.99991 11H6.99991Z" fill="#3C3833"/>
        <path d="M6.65689 5.94975L1.70715 1L1.00004 1.70711L5.94979 6.65685L6.65689 5.94975ZM6.65689 5.94975L5.94975 5.24272L1 10.1925L1.70711 10.8996L6.65689 5.94975ZM6.99991 1L6.99991 11H7.99991L7.99991 1L6.99991 1Z" stroke="#3C3833"/>
      </svg>
    </a>
    <a href="#" class="pagination__button pagination__button--prev" @click.prevent="prevPage" v-if="hasPrevPage" aria-label="Previous Page">
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.95012 6.65685L1.00037 1.70711L1.70748 1L6.65723 5.94975L5.95012 6.65685Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.00033 10.1925L5.95008 5.24272L6.65723 5.94975L1.70744 10.8996L1.00033 10.1925Z" fill="#3C3833"/>
        <path d="M6.65723 5.94975L1.70748 1L1.00037 1.70711L5.95012 6.65685L6.65723 5.94975ZM6.65723 5.94975L5.95008 5.24272L1.00033 10.1925L1.70744 10.8996L6.65723 5.94975Z" stroke="#3C3833"/>
      </svg>
    </a>
    <template v-for="p in visiblePages">
      <a href="#" class="pagination__page" :class="{ 'pagination__page--current': p === currentPage }" @click.prevent="goToPage(p)" :aria-label="`Go to Page ${p}`">{{ p }}</a>
    </template>
    <a href="#" class="pagination__button pagination__button--next" @click.prevent="nextPage" v-if="hasNextPage" aria-label="Next Page">
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.95012 6.65685L1.00037 1.70711L1.70748 1L6.65723 5.94975L5.95012 6.65685Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.00033 10.1925L5.95008 5.24272L6.65723 5.94975L1.70744 10.8996L1.00033 10.1925Z" fill="#3C3833"/>
        <path d="M6.65723 5.94975L1.70748 1L1.00037 1.70711L5.95012 6.65685L6.65723 5.94975ZM6.65723 5.94975L5.95008 5.24272L1.00033 10.1925L1.70744 10.8996L6.65723 5.94975Z" stroke="#3C3833"/>
      </svg>
    </a>
    <a href="#" class="pagination__button pagination__button--last" @click.prevent="goToPage(pages)" v-if="hasNextPage" aria-label="Last Page">
      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="12" viewBox="0 0 9 12" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.94979 6.65685L1.00004 1.70711L1.70715 1L6.65689 5.94975L5.94979 6.65685Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1 10.1925L5.94975 5.24272L6.65689 5.94975L1.70711 10.8996L1 10.1925Z" fill="#3C3833"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M6.99991 11L6.99991 1L7.99991 1L7.99991 11H6.99991Z" fill="#3C3833"/>
        <path d="M6.65689 5.94975L1.70715 1L1.00004 1.70711L5.94979 6.65685L6.65689 5.94975ZM6.65689 5.94975L5.94975 5.24272L1 10.1925L1.70711 10.8996L6.65689 5.94975ZM6.99991 1L6.99991 11H7.99991L7.99991 1L6.99991 1Z" stroke="#3C3833"/>
      </svg>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.pagination {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  justify-content: center;

  .pagination__button {
    &.pagination__button--disabled {

    }

    &.pagination__button--first svg, &.pagination__button--prev svg {
      transform: rotate(180deg);
    }

    svg {
      height: 10px;
      fill: var(--neutral-1000, #3C3833);
      stroke-width: 1px;
      stroke: var(--neutral-1000, #3C3833);
    }
  }
  .pagination__page {
    color: var(--neutral-700, #767370);
    font-size: 16px;
    font-weight: 400;
    line-height: 28px; /* 175% */
    text-decoration: none;

    &.pagination__page--current {
      color: var(--neutral-1000, #3C3833);
      font-size: 18px;
      font-weight: 600;
      line-height: 32px; /* 177.778% */
    }
  }
}
</style>
